import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { ThemeProvider } from '@material-ui/core/styles';
import { selectMainTheme } from 'app/store/fuse/settingsSlice';
import { Button, Dialog, DialogContent, DialogTitle, TextField, Checkbox, ListItemText, Chip, IconButton, Tooltip as MuiTooltip, Tabs, Tab } from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import { format } from 'date-fns';
import { hu } from 'date-fns/locale';
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ReferenceArea } from 'recharts';
import { TbWaveSine } from 'react-icons/tb';
import { PiSteps, PiClockCounterClockwiseDuotone, PiPulseDuotone, PiBracketsCurlyDuotone } from 'react-icons/pi';

import EdtDynamicCheckService from 'app/admin/edtDynamicCheck/service/EdtDynamicCheckService';
import { useOptimizationPipeline } from '../OptimizationPipelineProvider';

const PING_INTERVAL_MS = 15000;
const RENDER_THROTTLE_MS = 300;
const MAX_POINTS_ALL = 10000;
const DEFAULT_WINDOW_MS = 15 * 60 * 1000;
const MAX_TIMELINE_ITEMS = 200;
const DEBUG_EDT = true;

const pushEdtDebug = (...args) => {
    if (!DEBUG_EDT) return;
    if (typeof window !== 'undefined') {
        if (!window.__edtWsLog) window.__edtWsLog = [];
        window.__edtWsLog.push({ time: new Date().toISOString(), args });
    }
    console.log('[DynamicValidationPopup]', ...args);
};

const logEdt = (...args) => { pushEdtDebug(...args); };

const roundConfig = {
    base: { label: 'Base', bg: 'rgba(245,158,11,0.14)', chipBg: 'rgba(245,158,11,0.18)', chipColor: '#92400e' },
    best: { label: 'Best', bg: 'rgba(34,197,94,0.14)', chipBg: 'rgba(34,197,94,0.18)', chipColor: '#166534' },
};

const datasetOptions = [
    { value: 'values', label: 'Values' },
    { value: 'variances', label: 'Variances' },
    { value: 'sigmas', label: 'Sigmas' },
    { value: 'alarmVariables', label: 'Alarm variables' },
];

const boolText = (v) => (v ? 'True' : 'False');
const isPlainObject = (value) => value != null && typeof value === 'object' && !Array.isArray(value);
const toCount = (value) => isPlainObject(value) ? Object.keys(value).length : 0;

const toBooleanMeta = (value) => {
    if (typeof value === 'boolean') return value;
    if (isPlainObject(value)) return Object.values(value).some((v) => Boolean(v));
    return Boolean(value);
};

const sanitizeJsonString = (raw) => {
    if (typeof raw !== 'string') return raw;
    return raw.replace(/\bNaN\b/g, 'null').replace(/\bInfinity\b/g, 'null').replace(/\b-Infinity\b/g, 'null');
};

export default function DynamicValidationPopup() {
    const { setPopup, optimizationPipelineData, unitOptimizerLogDataList, ts, dynamicValidationSettings, setDynamicValidationSettings } = useOptimizationPipeline();
    const [baseState, setBaseState] = useState(null);
    const [bestState, setBestState] = useState(null);
    const mainTheme = useSelector(selectMainTheme);

    const parseAnyToEpoch = (v) => {
        if (v == null) return NaN;
        if (typeof v === 'number') return v;
        const s = String(v).trim();
        const m = s.match(/^(\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2})(?:\.(\d+))?(Z|[+-]\d{2}:\d{2})?$/);
        if (m) {
            const base = m[1];
            const frac = m[2] ? '.' + m[2].slice(0, 3) : '';
            const tz = m[3] || 'Z';
            const fixed = base + frac + tz;
            const t = Date.parse(fixed);
            return Number.isFinite(t) ? t : NaN;
        }
        const t = Date.parse(s);
        return Number.isFinite(t) ? t : NaN;
    };

    const toDateKey = (v) => {
        const t = parseAnyToEpoch(v);
        if (!Number.isFinite(t)) return null;
        return new Date(t).toISOString().slice(0, 10);
    };

    useEffect(() => {
        logEdt('init', { ts, edtDynamicCheck: optimizationPipelineData?.edtDynamicCheck, hasDynamicValidationSettings: !!dynamicValidationSettings, dynamicValidationSettings });
    }, []);

    useEffect(() => {
        const list = Array.isArray(unitOptimizerLogDataList) ? unitOptimizerLogDataList : [];
        if (!list.length) {
            logEdt('base/best state source list empty');
            setBaseState(null);
            setBestState(null);
            return;
        }
        const targetKey = toDateKey(ts);
        let entry = null;
        if (targetKey) entry = list.find((e) => toDateKey(e?.ts) === targetKey) || null;
        if (!entry) {
            let latest = null;
            let latestT = -Infinity;
            for (const e of list) {
                const te = parseAnyToEpoch(e?.ts);
                if (Number.isFinite(te) && te > latestT) {
                    latestT = te;
                    latest = e;
                }
            }
            entry = latest || null;
        }
        logEdt('resolved base/best state entry', { targetKey, entryTs: entry?.ts, hasBaseState: !!entry?.base_state, hasBestState: !!entry?.best_state });
        setBaseState(entry?.base_state ?? null);
        setBestState(entry?.best_state ?? null);
    }, [unitOptimizerLogDataList, ts]);

    const protocol = process.env.REACT_APP_WS_SECURE === 'true' ? 'wss' : 'ws';
    const host = (process.env.REACT_APP_WEBSOCKET === '' ? window.location.host : process.env.REACT_APP_WEBSOCKET) || window.location.host;
    const port = process.env.REACT_APP_WS_PORT && process.env.REACT_APP_WS_PORT.length ? ':' + process.env.REACT_APP_WS_PORT : '';
    const path = process.env.REACT_APP_EDT_WS_PATH || '';
    const wsUrl = protocol + '://' + host + port + path;

    const wsRef = useRef(null);
    const reconnectRef = useRef({ shouldReconnect: true, attempt: 0 });
    const reconnectTimerRef = useRef(null);
    const mountedRef = useRef(false);
    const bufferRef = useRef([]);
    const timelineRef = useRef([]);
    const rawEventsRef = useRef([]);
    const keysByDatasetRef = useRef({ values: new Set(), variances: new Set(), sigmas: new Set(), alarmVariables: new Set() });
    const visibleKeysByDatasetRef = useRef({ values: new Set(), variances: new Set(), sigmas: new Set(), alarmVariables: new Set() });

    const [wsState, setWsState] = useState('idle');
    const [isSubscribed, setIsSubscribed] = useState(false);
    const isSubscribedRef = useRef(false);
    const pingTimerRef = useRef(null);

    const [chartData, setChartData] = useState([]);
    const [timelineItems, setTimelineItems] = useState([]);
    const [rawEvents, setRawEvents] = useState([]);
    const [allKeys, setAllKeys] = useState([]);
    const [selectedKeys, setSelectedKeys] = useState([]);
    const [lastError, setLastError] = useState(null);
    const [windowMs] = useState(DEFAULT_WINDOW_MS);
    const [lineType, setLineType] = useState('step');
    const [isRunning, setIsRunning] = useState(false);
    const [datasetMode, setDatasetMode] = useState('values');
    const [rightTab, setRightTab] = useState('chart');
    const [latestStatus, setLatestStatus] = useState(null);

    const isRunningRef = useRef(false);
    const hasRunRef = useRef(false);
    const autoStartEnabledRef = useRef(true);
    const appendMessageRef = useRef(null);
    const handleStartRef = useRef(null);

    useEffect(() => { isRunningRef.current = isRunning; }, [isRunning]);

    const formatTs = (t) => {
        const d = new Date(Number(t));
        return Number.isFinite(d.getTime()) ? format(d, 'yyyy.MM.dd. HH:mm:ss', { locale: hu }) : '';
    };

    const xTick = (value) => Number.isFinite(Number(value)) ? String(value) : '';

    const normalizeNumericObject = (obj) => {
        if (!isPlainObject(obj)) return {};
        const out = {};
        Object.entries(obj).forEach(([k, v]) => {
            const n = Number(v);
            out[k] = Number.isFinite(n) ? n : null;
        });
        return out;
    };

    const pushTimelineItem = useCallback((item) => {
        const next = [...timelineRef.current, item].slice(-MAX_TIMELINE_ITEMS);
        timelineRef.current = next;
        setTimelineItems(next);
    }, []);

    const pushRawEvent = useCallback((item) => {
        const next = [...rawEventsRef.current, item].slice(-MAX_TIMELINE_ITEMS);
        rawEventsRef.current = next;
        setRawEvents(next);
    }, []);

    const syncKeysForDataset = useCallback((dataset, insertedKeys = []) => {
        const keys = Array.from(keysByDatasetRef.current[dataset]).sort();
        const vis = new Set(visibleKeysByDatasetRef.current[dataset]);
        insertedKeys.forEach((k) => vis.add(k));
        if (visibleKeysByDatasetRef.current[dataset].size === 0) keys.forEach((k) => vis.add(k));
        visibleKeysByDatasetRef.current[dataset] = vis;
        if (dataset === datasetMode) {
            setAllKeys(keys);
            setSelectedKeys(keys.filter((k) => vis.has(k)));
        }
    }, [datasetMode]);

    useEffect(() => {
        const keys = Array.from(keysByDatasetRef.current[datasetMode]).sort();
        const vis = visibleKeysByDatasetRef.current[datasetMode];
        setAllKeys(keys);
        setSelectedKeys(keys.filter((k) => vis.has(k)));
    }, [datasetMode]);

    const normalizeWsEvent = useCallback((msg) => {
        if (typeof msg !== 'object' || !msg) return null;
        if (msg.command === 'pong') return { kind: 'heartbeat', type: 'pong', ts: Date.now(), raw: msg };
        const type = msg.type || null;
        const payload = isPlainObject(msg.payload) ? msg.payload : {};
        const round = typeof payload.round === 'string' ? payload.round.toLowerCase() : null;
        if (type === 'initializing' || type === 'iterating-start') {
            return {
                kind: 'status',
                eventType: type,
                ts: Date.now(),
                round,
                name: msg.name ?? null,
                state: msg.state ?? null,
                message: payload.message ?? null,
                meta: msg.meta ?? null,
                raw: msg,
            };
        }
        if (type === 'iterating-values') {
            const t = parseAnyToEpoch(payload.time);
            const iterationNum = Number(payload.iteration);
            return {
                kind: 'point',
                eventType: type,
                ts: Number.isFinite(t) ? t : Date.now(),
                iteration: Number.isFinite(iterationNum) ? iterationNum : null,
                name: msg.name ?? null,
                round,
                finished: Boolean(payload.finished),
                alarm: Boolean(payload.alarm),
                converged: Boolean(payload.converged),
                sequenceFinishedTags: isPlainObject(payload.sequence_finished_tags) ? payload.sequence_finished_tags : {},
                alarmVariablesMeta: isPlainObject(payload.alarm_variables) ? payload.alarm_variables : {},
                values: normalizeNumericObject(payload.variables_to_monitor),
                variances: normalizeNumericObject(payload.variances),
                sigmas: normalizeNumericObject(payload.sigmas),
                alarmVariables: normalizeNumericObject(payload.alarm_variables),
                state: msg.state ?? null,
                meta: msg.meta ?? null,
                raw: msg,
            };
        }
        return { kind: 'unknown', ts: Date.now(), raw: msg, eventType: type || null };
    }, []);

    const upsertPoint = useCallback((p) => {
        const arr = bufferRef.current;
        if (arr.length === 0) {
            arr.push(p);
            return;
        }
        const sameIdentity = (a, b) => a && b && a.iteration != null && b.iteration != null && a.iteration === b.iteration && a.round === b.round;
        if (p.iteration != null) {
            const idx = arr.findIndex((r) => sameIdentity(r, p));
            if (idx >= 0) {
                arr[idx] = p;
                return;
            }
        }
        if (p.ts >= arr[arr.length - 1].ts) {
            arr.push(p);
            return;
        }
        let lo = 0;
        let hi = arr.length - 1;
        while (lo <= hi) {
            const mid = Math.floor((lo + hi) / 2);
            if (arr[mid].ts < p.ts) lo = mid + 1;
            else hi = mid - 1;
        }
        arr.splice(lo, 0, p);
    }, []);

    const pruneWindow = useCallback((now) => {
        const arr = bufferRef.current;
        if (!arr.length) return;
        if (windowMs == null) {
            if (arr.length > MAX_POINTS_ALL) bufferRef.current = arr.slice(arr.length - MAX_POINTS_ALL);
            return;
        }
        const fromTs = now - windowMs;
        let lo = 0;
        let hi = arr.length - 1;
        let idx = arr.length;
        while (lo <= hi) {
            const mid = Math.floor((lo + hi) / 2);
            if (arr[mid].ts >= fromTs) {
                idx = mid;
                hi = mid - 1;
            } else lo = mid + 1;
        }
        if (idx > 0) bufferRef.current = arr.slice(idx);
    }, [windowMs]);

    const setSubscribedSafe = useCallback((v) => {
        logEdt('setSubscribed()', { next: v });
        isSubscribedRef.current = v;
        setIsSubscribed(v);
    }, []);

    const send = useCallback((obj) => {
        try {
            const ws = wsRef.current;
            const readyState = ws?.readyState;
            logEdt('send()', { readyState, payload: obj });
            if (ws && readyState === WebSocket.OPEN) {
                ws.send(JSON.stringify(obj));
                logEdt('send() ok', obj);
            } else {
                logEdt('send() skipped - websocket not open', { readyState });
                setLastError('WebSocket not open');
            }
        } catch (e) {
            logEdt('send() failed', e);
            setLastError(String(e?.message || e));
        }
    }, []);

    const stopPing = useCallback(() => {
        if (pingTimerRef.current) {
            logEdt('stopPing()');
            window.clearInterval(pingTimerRef.current);
            pingTimerRef.current = null;
        }
    }, []);

    const startPing = useCallback(() => {
        stopPing();
        logEdt('startPing()', { intervalMs: PING_INTERVAL_MS });
        pingTimerRef.current = window.setInterval(() => {
            if (wsRef.current?.readyState === WebSocket.OPEN && isSubscribedRef.current) {
                logEdt('ping tick -> send ping');
                send({ command: 'ping' });
            } else {
                logEdt('ping tick skipped', { readyState: wsRef.current?.readyState, isSubscribed: isSubscribedRef.current });
            }
        }, PING_INTERVAL_MS);
    }, [send, stopPing]);

    const signup = useCallback(() => {
        const edtDynamicCheckId = optimizationPipelineData?.edtDynamicCheck;
        const readyState = wsRef.current?.readyState;
        logEdt('signup() entered', { edtDynamicCheckId, readyState });
        if (!edtDynamicCheckId) {
            setLastError('Missing edtDynamicCheck id');
            return false;
        }
        if (readyState !== WebSocket.OPEN) {
            setLastError('WebSocket not open');
            return false;
        }
        if (isSubscribedRef.current) return true;
        send({ command: 'signup', id: edtDynamicCheckId });
        setSubscribedSafe(true);
        startPing();
        return true;
    }, [optimizationPipelineData?.edtDynamicCheck, send, setSubscribedSafe, startPing]);

    const resign = useCallback(() => {
        logEdt('resign()');
        stopPing();
        if (wsRef.current?.readyState === WebSocket.OPEN && isSubscribedRef.current) send({ command: 'resign' });
        setSubscribedSafe(false);
    }, [send, setSubscribedSafe, stopPing]);

    const payloadFromSettings = useMemo(() => {
        const baseValues = isPlainObject(dynamicValidationSettings?.base) ? dynamicValidationSettings.base : null;
        const bestValues = isPlainObject(dynamicValidationSettings?.best) ? dynamicValidationSettings.best : null;
        return { base: baseValues, best: bestValues };
    }, [dynamicValidationSettings]);

    const doStart = useCallback(async () => {
        const simId = optimizationPipelineData?.edtDynamicCheck;
        const resolvedBestCase = payloadFromSettings.best ?? bestState ?? baseState ?? null;
        const resolvedBaseCase = payloadFromSettings.base ?? baseState ?? resolvedBestCase ?? null;
        logEdt('doStart() entered', {
            hasRun: hasRunRef.current, simId, hasPayloadBase: !!payloadFromSettings.base, hasPayloadBest: !!payloadFromSettings.best, hasBestState: !!bestState, hasBaseState: !!baseState,
            baseCaseKeys: resolvedBaseCase && typeof resolvedBaseCase === 'object' ? Object.keys(resolvedBaseCase) : null,
            bestCaseKeys: resolvedBestCase && typeof resolvedBestCase === 'object' ? Object.keys(resolvedBestCase) : null, dynamicValidationSettings
        });
        if (hasRunRef.current) {
            logEdt('doStart() skipped - already started');
            return true;
        }
        if (!simId || !resolvedBestCase || !resolvedBaseCase) {
            logEdt('doStart() blocked - missing simId or payload', { simId, hasBaseCase: !!resolvedBaseCase, hasBestCase: !!resolvedBestCase });
            setLastError('Missing edtDynamicCheck or base_case / best_case for start');
            return false;
        }
        try {
            hasRunRef.current = true;
            const res = await EdtDynamicCheckService.start(simId, resolvedBaseCase, resolvedBestCase);
            logEdt('service.start() resolved', { response: res, request: { id: simId, baseValues: resolvedBaseCase, bestValues: resolvedBestCase } });
            return true;
        } catch (e) {
            hasRunRef.current = false;
            logEdt('service.start() failed', e);
            setLastError('start() failed: ' + String(e?.message || e));
            setDynamicValidationSettings(null);
            return false;
        }
    }, [optimizationPipelineData?.edtDynamicCheck, payloadFromSettings, bestState, baseState, setDynamicValidationSettings, dynamicValidationSettings]);

    const terminateSim = useCallback(async () => {
        try {
            const simId = optimizationPipelineData?.edtDynamicCheck;
            logEdt('terminateSim() entered', { simId });
            if (simId) {
                const res = await EdtDynamicCheckService.terminate(simId);
                logEdt('terminateSim() resolved', res);
            } else logEdt('terminateSim() skipped - missing simId');
        } catch (e) {
            logEdt('terminateSim() failed', e);
            setLastError('terminate() failed: ' + String(e?.message || e));
        }
    }, [optimizationPipelineData?.edtDynamicCheck]);

    const handleStart = useCallback(async () => {
        autoStartEnabledRef.current = true;
        logEdt('handleStart()');
        if (wsRef.current?.readyState !== WebSocket.OPEN) {
            setLastError('WebSocket not open');
            return;
        }
        const signedUpNow = !isSubscribedRef.current ? signup() : true;
        if (!signedUpNow) return;
        const started = await doStart();
        if (started) setIsRunning(true);
        else if (!isRunningRef.current) resign();
    }, [doStart, resign, signup]);

    useEffect(() => { handleStartRef.current = handleStart; }, [handleStart]);

    const handleTerminate = useCallback(async () => {
        autoStartEnabledRef.current = false;
        logEdt('handleTerminate()');
        await terminateSim();
        hasRunRef.current = false;
        setIsRunning(false);
        resign();
    }, [resign, terminateSim]);

    const handleClose = useCallback(async () => {
        autoStartEnabledRef.current = false;
        logEdt('handleClose()');
        if (isRunningRef.current) await terminateSim();
        hasRunRef.current = false;
        setIsRunning(false);
        resign();
        setDynamicValidationSettings(null);
        setPopup(null);
    }, [resign, terminateSim, setDynamicValidationSettings, setPopup]);

    const toggleKey = useCallback((key) => {
        if (!key) return;
        const vis = new Set(visibleKeysByDatasetRef.current[datasetMode]);
        if (vis.has(key)) vis.delete(key);
        else vis.add(key);
        visibleKeysByDatasetRef.current[datasetMode] = vis;
        const keys = Array.from(keysByDatasetRef.current[datasetMode]).sort();
        setSelectedKeys(keys.filter((k) => vis.has(k)));
        logEdt('toggleKey()', { datasetMode, key, selectedKeys: keys.filter((k) => vis.has(k)) });
    }, [datasetMode]);

    const handleKeysChange = (e, newValue) => {
        const next = Array.isArray(newValue) ? newValue : [];
        const nextSet = new Set(next);
        visibleKeysByDatasetRef.current[datasetMode] = nextSet;
        const keys = Array.from(keysByDatasetRef.current[datasetMode]).sort();
        setSelectedKeys(keys.filter((k) => nextSet.has(k)));
        logEdt('handleKeysChange', { datasetMode, next });
    };

    const appendMessage = useCallback((raw) => {
        let parsed = null;
        try {
            parsed = JSON.parse(sanitizeJsonString(raw));
        } catch (e) {
            logEdt('appendMessage() invalid JSON', { raw, error: e });
            pushTimelineItem({ id: 'json-' + Date.now() + '-' + Math.random(), ts: Date.now(), round: null, type: 'parse-error', message: 'Invalid JSON message received', raw: raw.slice(0, 500) });
            return;
        }

        const normalized = normalizeWsEvent(parsed);
        pushRawEvent({ id: 'raw-' + Date.now() + '-' + Math.random(), ts: Date.now(), eventType: normalized?.eventType || parsed?.type || parsed?.command || 'unknown', data: parsed });
        if (!normalized) return;

        if (normalized.kind === 'heartbeat') {
            logEdt('appendMessage() heartbeat', normalized);
            return;
        }

        if (normalized.kind === 'status') {
            setLatestStatus(normalized);
            pushTimelineItem({
                id: 'status-' + normalized.ts + '-' + Math.random(),
                ts: normalized.ts,
                round: normalized.round,
                type: normalized.eventType,
                message: normalized.message || normalized.eventType,
                iteration: normalized.iteration ?? null,
            });
            logEdt('appendMessage() status accepted', normalized);
            return;
        }

        if (normalized.kind === 'point') {
            setLatestStatus(normalized);
            ['values', 'variances', 'sigmas', 'alarmVariables'].forEach((dataset) => {
                const source = normalized[dataset] || {};
                const insertedKeys = [];
                Object.keys(source).forEach((k) => {
                    if (!keysByDatasetRef.current[dataset].has(k)) {
                        keysByDatasetRef.current[dataset].add(k);
                        insertedKeys.push(k);
                    }
                });
                if (insertedKeys.length > 0) syncKeysForDataset(dataset, insertedKeys);
            });

            upsertPoint(normalized);
            pushTimelineItem({
                id: 'point-' + normalized.ts + '-' + normalized.iteration + '-' + normalized.round,
                ts: normalized.ts,
                round: normalized.round,
                type: normalized.eventType,
                message: 'Iteration ' + (normalized.iteration ?? '-'),
                iteration: normalized.iteration,
                finished: normalized.finished,
                alarm: normalized.alarm,
                converged: normalized.converged,
            });

            logEdt('appendMessage() point accepted', {
                ts: normalized.ts, iteration: normalized.iteration, round: normalized.round, alarm: normalized.alarm, converged: normalized.converged,
                valueKeys: Object.keys(normalized.values || {}), varianceKeys: Object.keys(normalized.variances || {}), sigmaKeys: Object.keys(normalized.sigmas || {}),
                alarmVariableKeys: Object.keys(normalized.alarmVariables || {}), bufferLength: bufferRef.current.length
            });
            return;
        }

        pushTimelineItem({ id: 'unknown-' + Date.now() + '-' + Math.random(), ts: Date.now(), round: null, type: 'unknown', message: normalized.eventType || 'Unknown event' });
        logEdt('appendMessage() unknown event', normalized);
    }, [normalizeWsEvent, pushRawEvent, pushTimelineItem, syncKeysForDataset, upsertPoint]);

    useEffect(() => { appendMessageRef.current = appendMessage; }, [appendMessage]);

    useEffect(() => {
        mountedRef.current = true;
        reconnectRef.current.shouldReconnect = true;

        const connect = () => {
            const s = wsRef.current?.readyState;
            logEdt('connect() called', { wsUrl, readyState: s, attempt: reconnectRef.current.attempt });
            if (s === WebSocket.OPEN || s === WebSocket.CONNECTING) {
                logEdt('connect() skipped - already open/connecting', { readyState: s });
                return;
            }
            setLastError(null);
            setWsState('connecting');
            logEdt('creating websocket', { wsUrl });
            const ws = new WebSocket(wsUrl);
            wsRef.current = ws;

            ws.onopen = () => {
                if (!mountedRef.current) return;
                logEdt('ws.onopen', { wsUrl });
                setWsState('open');
                reconnectRef.current.attempt = 0;
                if (autoStartEnabledRef.current) handleStartRef.current?.();
            };

            ws.onmessage = (evt) => {
                if (!mountedRef.current) return;
                logEdt('ws.onmessage', evt.data);
                appendMessageRef.current?.(evt.data);
            };

            ws.onerror = (err) => {
                if (!mountedRef.current) return;
                logEdt('ws.onerror', err);
                setWsState('error');
                setLastError(String(err?.message || 'WebSocket error'));
            };

            ws.onclose = (evt) => {
                if (!mountedRef.current) return;
                logEdt('ws.onclose', { code: evt?.code, reason: evt?.reason, wasClean: evt?.wasClean, shouldReconnect: reconnectRef.current.shouldReconnect });
                setWsState('closed');
                stopPing();
                setSubscribedSafe(false);

                if (reconnectRef.current.shouldReconnect) {
                    const attempt = reconnectRef.current.attempt || 0;
                    const backoffMs = Math.min(10000, 1000 * Math.pow(2, attempt));
                    reconnectRef.current.attempt = attempt + 1;
                    logEdt('reconnect scheduled', { nextAttempt: reconnectRef.current.attempt, backoffMs });
                    if (reconnectTimerRef.current) window.clearTimeout(reconnectTimerRef.current);
                    reconnectTimerRef.current = window.setTimeout(() => {
                        if (mountedRef.current && reconnectRef.current.shouldReconnect) connect();
                    }, backoffMs);
                }
            };
        };

        connect();

        return () => {
            logEdt('cleanup websocket effect');
            mountedRef.current = false;
            reconnectRef.current.shouldReconnect = false;
            if (reconnectTimerRef.current) {
                window.clearTimeout(reconnectTimerRef.current);
                reconnectTimerRef.current = null;
            }
            stopPing();
            try { wsRef.current?.close(); } catch (e) { logEdt('cleanup close failed', e); }
            wsRef.current = null;
            setSubscribedSafe(false);
        };
    }, [wsUrl, setSubscribedSafe, stopPing]);

    useEffect(() => {
        let t = null;
        const tick = () => {
            if (!mountedRef.current) return;
            const now = Date.now();
            pruneWindow(now);

            const activeKeys = Array.from(visibleKeysByDatasetRef.current[datasetMode]);
            const rows = bufferRef.current.map((r) => {
                const source = r[datasetMode] || {};
                const row = {
                    ts: r.ts, iteration: r.iteration, name: r.name, round: r.round, finished: r.finished, alarm: r.alarm, converged: r.converged,
                    sequenceFinishedTags: r.sequenceFinishedTags, alarmVariablesMeta: r.alarmVariablesMeta, meta: r.meta ?? null
                };
                activeKeys.forEach((k) => { row[k] = source[k] == null ? null : source[k]; });
                return row;
            });
            setChartData(rows);
            t = window.setTimeout(tick, RENDER_THROTTLE_MS);
        };
        t = window.setTimeout(tick, RENDER_THROTTLE_MS);
        return () => { if (t) window.clearTimeout(t); };
    }, [datasetMode, pruneWindow]);

    const colorForKey = (key, fallback = '#252f3e') => {
        if (!key) return fallback;
        let h = 0;
        for (let i = 0; i < key.length; i += 1) {
            h = (Math.imul(h, 31) + key.charCodeAt(i)) % 4294967296;
            if (h < 0) h += 4294967296;
        }
        const hue = h % 360;
        return `hsl(${hue},70%,40%)`;
    };

    const strokeForKey = (key) => selectedKeys.length === 1 && selectedKeys[0] === key ? '#252f3e' : colorForKey(key);
    const pointRows = bufferRef.current;
    const isEmpty = pointRows.length === 0;
    const latestPoint = pointRows.length ? pointRows[pointRows.length - 1] : null;

    const socketStatus = useMemo(() => {
        if (wsState === 'open') return { label: 'Connected', bg: 'rgba(34,197,94,0.18)', color: '#166534' };
        if (wsState === 'connecting') return { label: 'Connecting', bg: 'rgba(59,130,246,0.16)', color: '#1d4ed8' };
        if (wsState === 'error') return { label: 'Error', bg: 'rgba(239,68,68,0.18)', color: '#991b1b' };
        return { label: 'Disconnected', bg: 'rgba(148,163,184,0.16)', color: '#475569' };
    }, [wsState]);

    const roundBands = useMemo(() => {
        const source = chartData.filter((row) => row?.round && Number.isFinite(Number(row?.iteration)));
        if (!source.length) return [];
        const bands = [];
        let startIndex = 0;
        for (let i = 1; i <= source.length; i += 1) {
            const prev = source[i - 1];
            const curr = source[i];
            if (!curr || curr.round !== prev.round) {
                const startValue = Number(source[startIndex].iteration);
                const endValue = Number(source[i - 1].iteration);
                const prevValue = startIndex > 0 ? Number(source[startIndex - 1].iteration) : startValue;
                const nextValue = i < source.length ? Number(source[i].iteration) : endValue;
                let x1 = startIndex === 0 ? startValue : (prevValue + startValue) / 2;
                let x2 = i === source.length ? endValue : (endValue + nextValue) / 2;
                if (x2 < x1) {
                    const tmp = x1;
                    x1 = x2;
                    x2 = tmp;
                }
                bands.push({ x1, x2, round: source[startIndex].round });
                startIndex = i;
            }
        }
        return bands;
    }, [chartData]);

    const renderAlarmDot = useCallback((props) => {
        const { cx, cy, payload, value } = props;
        if (!payload?.alarm || value == null || cx == null || cy == null) return null;
        return <circle cx={cx} cy={cy} r={4} fill="#dc2626" stroke="#ffffff" strokeWidth={2} />;
    }, []);

    const renderStatusChip = (label, value, activeStyle, inactiveStyle = null) => (
        <Chip size="small" className='flex-1' label={label + ': ' + value} style={{ fontSize: 12, height: 24, ...(inactiveStyle || { backgroundColor: 'rgba(15,23,42,0.06)', color: '#334155' }), ...(value === 'True' ? activeStyle : {}) }} />
    );

    const renderCountChip = (label, value, style = {}) => (
        <Chip size="small" className='flex-1' label={label + ': ' + value} style={{ fontSize: 12, height: 24, backgroundColor: 'rgba(15,23,42,0.06)', color: '#334155', ...style }} />
    );

    const CustomLegend = () => (
        <div className="flex flex-wrap items-center justify-center gap-x-16 gap-y-8 pt-16">
            {allKeys.map((key) => {
                const active = selectedKeys.includes(key);
                const stroke = strokeForKey(key);
                return (
                    <button key={key} type="button" onClick={() => toggleKey(key)} className="flex items-center gap-8 text-xs" style={{ opacity: active ? 1 : 0.45, background: 'transparent', border: 0, padding: 0, cursor: 'pointer' }}>
                        <span style={{ width: 18, height: 0, borderTop: '3px solid ' + stroke, display: 'inline-block' }} />
                        <span>{key}</span>
                    </button>
                );
            })}
            <span className="mx-4 h-16 w-px bg-black opacity-10" />
            <div className="flex items-center gap-8 text-xs"><span style={{ width: 12, height: 12, backgroundColor: roundConfig.base.bg, border: '1px solid rgba(0,0,0,0.08)', display: 'inline-block' }} /><span>Base background</span></div>
            <div className="flex items-center gap-8 text-xs"><span style={{ width: 12, height: 12, backgroundColor: roundConfig.best.bg, border: '1px solid rgba(0,0,0,0.08)', display: 'inline-block' }} /><span>Best background</span></div>
        </div>
    );

    const TooltipContent = ({ active, payload, label }) => {
        if (!active || !payload || payload.length === 0) return null;
        const p = payload[0]?.payload || {};
        const tsText = formatTs(p.ts);
        const iterationText = Number.isFinite(p.iteration) ? String(p.iteration) : String(label ?? '-');
        const roundText = p.round || '-';
        const metaText = p.meta ? JSON.stringify(p.meta) : '-';
        return (
            <div className='flex flex-col gap-1 bg-black opacity-80 p-8 text-white text-xs shadow-lg min-w-[220px] max-w-[380px]'>
                <div className='mb-2 font-semibold text-[11px] opacity-80'>Iteration {iterationText}</div>
                <div className='mb-1'><span className='opacity-70 mr-2'>time:</span><span className='font-medium'>{tsText}</span></div>
                <div className='mb-1'><span className='opacity-70 mr-2'>name:</span><span className='font-medium'>{p.name || '-'}</span></div>
                <div className='mb-1'><span className='opacity-70 mr-2'>round:</span><span className='font-medium'>{roundText}</span></div>
                <div className='mb-1'><span className='opacity-70 mr-2'>finished:</span><span className='font-medium'>{boolText(Boolean(p.finished))}</span></div>
                <div className='mb-1'><span className='opacity-70 mr-2'>alarm:</span><span className='font-medium'>{boolText(Boolean(p.alarm))}</span></div>
                <div className='mb-1'><span className='opacity-70 mr-2'>converged:</span><span className='font-medium'>{boolText(Boolean(p.converged))}</span></div>
                <div className='mb-1'><span className='opacity-70 mr-2'>sequence tags:</span><span className='font-medium'>{toCount(p.sequenceFinishedTags)}</span></div>
                <div className='mb-2'><span className='opacity-70 mr-2'>alarm vars:</span><span className='font-medium'>{toCount(p.alarmVariablesMeta)}</span></div>
                <div className='mb-2 break-all'><span className='opacity-70 mr-2'>meta:</span><span className='font-medium'>{metaText}</span></div>
                {payload.map((entry) => <div key={entry.dataKey} className='font-medium'>{entry.name}: {entry.value == null ? '-' : entry.value}</div>)}
            </div>
        );
    };

    const latestAlarmVariableKeys = useMemo(() => latestPoint?.alarmVariablesMeta ? Object.keys(latestPoint.alarmVariablesMeta) : [], [latestPoint]);
    const latestSequenceTagKeys = useMemo(() => latestPoint?.sequenceFinishedTags ? Object.keys(latestPoint.sequenceFinishedTags) : [], [latestPoint]);

    return (
        <ThemeProvider theme={mainTheme}>
            <Dialog open fullWidth maxWidth="xl">
                <DialogTitle disableTypography className="border-b flex flex-col px-0">
                    <div className="flex items-center justify-between gap-32 px-32 pb-14">
                        <div className="whitespace-nowrap font-semibold">EDT Dynamic Check</div>
                        <div className="flex items-center gap-12">
                            <Button size="small" onClick={isRunning ? handleTerminate : handleStart} disabled={!isRunning && wsState !== 'open'}>{isRunning ? 'Terminate' : 'Start'}</Button>
                            <Button onClick={handleClose}>Close</Button>
                        </div>
                    </div>

                    <div className="w-full flex flex-col gap-8 shadow-inner px-20 pt-14">
                        <div className="w-full flex flex-col gap-10 rounded-xl bg-grey-100 py-10 shadow px-16">
                            <div className="w-full flex items-center gap-12">
                                <Tabs value={datasetMode} onChange={(e, v) => setDatasetMode(v)} indicatorColor="primary" textColor="primary" variant="scrollable" scrollButtons="auto">
                                    {datasetOptions.map((item) => <Tab key={item.value} value={item.value} label={item.label} />)}
                                </Tabs>

                                <Autocomplete
                                    className="flex-1"
                                    multiple
                                    options={allKeys}
                                    value={selectedKeys}
                                    onChange={handleKeysChange}
                                    disableCloseOnSelect
                                    getOptionLabel={(option) => option}
                                    renderOption={(option, { selected }) => (<><Checkbox style={{ marginRight: 8 }} checked={selected} /><ListItemText primary={option} /></>)}
                                    renderTags={(value, getTagProps) => value.map((option, index) => <Chip key={option} label={option} {...getTagProps({ index })} size="small" style={{ padding: '5px 5px', marginRight: 4, marginBottom: 4, fontSize: '12px' }} />)}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            variant="outlined"
                                            label="Select attributes"
                                            placeholder="Search…"
                                            InputLabelProps={{ shrink: true }}
                                            InputProps={{ ...params.InputProps, style: { minHeight: 40 }, inputProps: { ...params.inputProps, style: { padding: '10px 8px', fontSize: '13px', lineHeight: 1.5 } } }}
                                        />
                                    )}
                                />

                                <MuiTooltip title="Toggle line type" arrow>
                                    <span><IconButton size="medium" onClick={() => setLineType((prev) => (prev === 'monotone' ? 'step' : 'monotone'))}>{lineType === 'monotone' ? <PiSteps size={20} /> : <TbWaveSine size={20} />}</IconButton></span>
                                </MuiTooltip>
                            </div>

                            {lastError && <div className="text-xs text-red-600">{lastError}</div>}
                        </div>
                    </div>

                    <div className='w-full flex px-20 pt-14'>
                        <div className="w-full flex flex-wrap items-center gap-8">
                            <Chip size="small" className='flex-1' label={'Status: ' + socketStatus.label} style={{ fontSize: 12, height: 24, backgroundColor: socketStatus.bg, color: socketStatus.color }} />
                            <Chip size="small" className='flex-1' label={'Round: ' + (roundConfig[latestPoint?.round]?.label || (latestPoint?.round || latestStatus?.round || '-'))} style={{ fontSize: 12, height: 24, backgroundColor: roundConfig[latestPoint?.round || latestStatus?.round]?.chipBg || 'rgba(15,23,42,0.06)', color: roundConfig[latestPoint?.round || latestStatus?.round]?.chipColor || '#334155' }} />
                            {renderCountChip('Iteration', Number.isFinite(latestPoint?.iteration) ? latestPoint.iteration : '-')}
                            {renderStatusChip('Alarm', boolText(Boolean(latestPoint?.alarm)), { backgroundColor: 'rgba(239,68,68,0.18)', color: '#991b1b' })}
                            {renderStatusChip('Finished', boolText(Boolean(latestPoint?.finished)), { backgroundColor: 'rgba(59,130,246,0.16)', color: '#1d4ed8' })}
                            {renderStatusChip('Converged', boolText(Boolean(latestPoint?.converged)), { backgroundColor: 'rgba(34,197,94,0.18)', color: '#166534' })}
                            {renderCountChip('Alarm variables', latestAlarmVariableKeys.length)}
                            {renderCountChip('Sequence tags', latestSequenceTagKeys.length)}
                        </div>
                    </div>

                    <div className='w-full flex px-20 pt-10 pb-4'>
                        <div className='w-full text-xs opacity-70'>
                            {latestStatus?.message ? latestStatus.message : 'Waiting for status messages'}
                        </div>
                    </div>
                </DialogTitle>

                <DialogContent className="w-full flex flex-col p-20 overflow-hidden" style={{ height: '700px' }}>
                    <div className="w-full h-full flex gap-16 overflow-hidden">
                        {/* <div className="w-[340px] min-w-[340px] h-full flex flex-col rounded-xl border bg-white overflow-hidden">
                            <div className="flex items-center justify-between px-12 py-10 border-b">
                                <div className="text-sm font-medium flex items-center gap-8"><PiClockCounterClockwiseDuotone size={18} />Timeline</div>
                                <div className="text-xs opacity-60">{timelineItems.length} items</div>
                            </div>
                            <div className="flex-1 overflow-auto p-10">
                                <div className="flex flex-col gap-8">
                                    {timelineItems.length === 0 ? <div className="text-xs opacity-60 p-8">No events yet</div> : timelineItems.slice().reverse().map((item) => (
                                        <div key={item.id} className="rounded-lg border px-10 py-8">
                                            <div className="flex items-center justify-between gap-8">
                                                <div className="text-[11px] font-semibold">{item.type}</div>
                                                <div className="text-[10px] opacity-60">{item.round || '-'}</div>
                                            </div>
                                            <div className="mt-4 text-xs">{item.message}</div>
                                            <div className="mt-4 flex items-center justify-between gap-8 text-[10px] opacity-60">
                                                <span>{item.iteration != null ? 'Iter ' + item.iteration : '-'}</span>
                                                <span>{formatTs(item.ts)}</span>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div> */}

                        <div className="flex-1 h-full flex flex-col rounded-xl border bg-white overflow-hidden">
                            <div className="flex items-center justify-between px-12 py-10 border-b">
                                {/* <div className="flex items-center gap-8 text-sm font-medium"><PiPulseDuotone size={18} />Visualization</div> */}
                                <Tabs value={rightTab} onChange={(e, v) => setRightTab(v)} indicatorColor="primary" textColor="primary">
                                    <Tab value="chart" label="Chart" />
                                    <Tab value="debug" label="Debug" />
                                </Tabs>
                            </div>

                            {rightTab === 'chart' ? (
                                <div className="flex-1 flex flex-col overflow-hidden">
                                    <div className="px-12 py-10 border-b text-xs opacity-70">
                                        Dataset: <strong>{datasetOptions.find((d) => d.value === datasetMode)?.label}</strong> · X axis: <strong>Iteration</strong> · Selected: <strong>{selectedKeys.length}</strong>
                                    </div>
                                    <div className="flex-1 p-12 overflow-hidden">
                                        <div className="w-full h-full">
                                            {isEmpty ? (
                                                <div className="w-full h-full flex items-center justify-center text-sm opacity-70">Waiting for data</div>
                                            ) : (
                                                <ResponsiveContainer width="100%" height="100%">
                                                    <LineChart data={chartData} margin={{ top: 10, left: selectedKeys.length === 0 ? -20 : 10, right: 10, bottom: 10 }}>
                                                        {roundBands.map((band, index) => <ReferenceArea key={band.round + '-' + band.x1 + '-' + band.x2 + '-' + index} x1={band.x1} x2={band.x2} fill={roundConfig[band.round]?.bg || 'rgba(148,163,184,0.10)'} strokeOpacity={0} />)}
                                                        <CartesianGrid strokeDasharray="3 3" />
                                                        <XAxis dataKey="iteration" tickMargin={10} tickFormatter={xTick} tickCount={10} type="number" domain={['dataMin', 'dataMax']} />
                                                        <YAxis tickMargin={10} tickCount={10} type="number" domain={['auto', 'auto']} />
                                                        <Tooltip content={<TooltipContent />} cursor={{ fill: 'transparent' }} wrapperStyle={{ position: 'absolute', zIndex: 1000 }} />
                                                        <Legend content={<CustomLegend />} />
                                                        {selectedKeys.map((key) => (
                                                            <Line key={key} type={lineType} dataKey={key} name={key} stroke={strokeForKey(key)} strokeWidth={2} dot={renderAlarmDot} isAnimationActive={false} connectNulls />
                                                        ))}
                                                    </LineChart>
                                                </ResponsiveContainer>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <div className="flex-1 overflow-auto p-12">
                                    <div className="mb-10 flex items-center gap-8 text-sm font-medium"><PiBracketsCurlyDuotone size={18} />Raw event log</div>
                                    <div className="flex flex-col gap-8">
                                        {rawEvents.length === 0 ? <div className="text-xs opacity-60">No raw events yet</div> : rawEvents.slice().reverse().map((item) => (
                                            <div key={item.id} className="rounded-lg border p-10">
                                                <div className="mb-6 flex items-center justify-between gap-8">
                                                    <div className="text-xs font-semibold">{item.eventType}</div>
                                                    <div className="text-[10px] opacity-60">{formatTs(item.ts)}</div>
                                                </div>
                                                <pre className="m-0 whitespace-pre-wrap break-all text-[11px] leading-relaxed opacity-80">{JSON.stringify(item.data, null, 2)}</pre>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
        </ThemeProvider>
    );
}